import React, { useState, useEffect, useRef } from "react";
import { History } from "lucide-react";
import { Toaster, toast } from "react-hot-toast";
import { GoCheckCircleFill } from "react-icons/go";
import { useLogin } from "./features/auth/hooks/useLogin";
import { useTranslator } from "./features/translator/hooks/useTranslator";
import { AuthPage } from "./features/auth/components/AuthPage";
import { AppHeader } from "./features/translator/components/AppHeader";
import { TranslationHistory } from "./features/history/components/TranslationHistory";
import { SubtitleView } from "./features/translator/components/SubtitleView";
import { MinutaView } from "./features/translator/components/MinutaView";
import { SettingsModal } from "./features/translator/components/SettingsModal";
import { DocsModal } from "./features/translator/components/DocsModal";
import { SummaryModal } from "./features/translator/components/SummaryModal";
import { ProfileModal } from "./features/auth/components/ProfileModal";
import { HistoryModal } from "./features/history/components/HistoryModal";
import { StopConfirmModal } from "./features/translator/components/StopConfirmModal";
import { UiLangProvider, useUiLang } from "./context/UiLangContext";
import { useAppModals } from "./hooks/useAppModals";
import { useAppShortcuts } from "./hooks/useAppShortcuts";
import { ModalsContainer } from "./components/ModalsContainer";
import "./index.css";

// ── Inner app (must be inside UiLangProvider to use useUiLang) ───────────────
function AppInner() {
  const auth = useTranslator();
  const login = useLogin();
  const { uiLang } = useUiLang();
  const isEn = uiLang === "en";
  const modals = useAppModals();
  const [theme, setTheme] = useState<"light" | "dark">("dark");

  // Load theme from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem("theme") as "light" | "dark" | null;
    const initial = saved || "dark";
    setTheme(initial);
    document.documentElement.classList.toggle("dark", initial === "dark");
  }, []);

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    localStorage.setItem("theme", next);
    document.documentElement.classList.toggle("dark", next === "dark");
  };

  const handleToggleRecording = () => {
    if (auth.recording) {
      modals.setStopConfirmOpen(true);
    } else {
      auth.toggleRecording();
    }
  };

  const previousRecording = useRef(auth.recording);
  const transcriptLengthRef = useRef(auth.meetingTranscript.length);

  useEffect(() => {
    transcriptLengthRef.current = auth.meetingTranscript.length;
  }, [auth.meetingTranscript.length]);

  useEffect(() => {
    if (!auth.recording && previousRecording.current) {
      toast.success(
        isEn 
          ? `Session ended · ${transcriptLengthRef.current} segments captured` 
          : `Sesión finalizada · ${transcriptLengthRef.current} segmentos capturados`,
        { duration: 4000, icon: <GoCheckCircleFill style={{ color: "#10B981" }} size={18} /> }
      );
    }
    previousRecording.current = auth.recording;
  }, [auth.recording, isEn]);

  // ── Global keyboard shortcuts (`Ctrl+M`, `Space`) centralized in hook ──
  useAppShortcuts({ auth, modals, isEn });

  // ── Loading spinner while session is being restored ──────────────────────────
  if (login.isInitializing) {
    return (
      <div className={`flex items-center justify-center min-h-screen w-screen transition-all text-[var(--text)] ${theme === "dark" ? "bg-[#1e1e22]" : "bg-white"}`}>
        <div className="w-10 h-10 border-4 border-accent/20 border-t-accent rounded-full animate-spin" />
      </div>
    );
  }

  // ── Auth screens ─────────────────────────────────────────────────────────────
  if (login.step !== "AUTHENTICATED") {
    return <AuthPage auth={login} theme={theme} toggleTheme={toggleTheme} />;
  }

  // ── Main app ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col w-screen h-screen text-[var(--text)] overflow-hidden font-sans relative transition-colors duration-300" style={{ background: 'var(--bg)' }}>
      <AppHeader
        appMode={auth.appMode}
        recording={auth.recording}
        recordingDuration={auth.recordingDuration}
        status={auth.status}
        voiceEnabled={auth.voiceEnabled}
        pipActive={auth.pipActive}
        statusText={auth.statusText}
        srcLang={auth.srcLang}
        tgtLang={auth.tgtLang}
        theme={theme}
        meetingTranscriptLength={auth.meetingTranscript.length}
        getStatusColorClass={auth.getStatusColorClass}
        setAppMode={auth.setAppMode}
        setVoiceEnabled={auth.setVoiceEnabled}
        setSrcLang={auth.setSrcLang}
        setTgtLang={auth.setTgtLang}
        onTogglePip={auth.togglePip}
        onToggleTheme={toggleTheme}
        onOpenDocs={() => modals.setDocsOpen(true)}
        onOpenSummary={() => modals.setSummaryOpen(true)}
        onToggleRecording={handleToggleRecording}
        onLogout={() => modals.setLogoutConfirmOpen(true)}
        onOpenProfile={() => modals.setProfileOpen(true)}
        onOpenSettings={() => modals.setSettingsOpen(true)}
      />

      <Toaster 
        position="bottom-right" 
        toastOptions={{
          style: {
            background: "var(--surface)",
            color: "var(--text)",
            border: "1px solid var(--border)",
            fontSize: "13px",
            borderRadius: "10px"
          },
        }}
      />

      {/* Main content */}
      <main className="flex-1 flex flex-col lg:flex-row overflow-hidden min-h-0 relative">
        {/* Mobile Top Controls (Status & History) */}
        <div className="lg:hidden flex flex-row items-center gap-3 px-3 py-3 bg-[var(--bg2)] flex-shrink-0 z-10 w-full">
          {/* History Button */}
          <button
            onClick={() => modals.setHistoryOpen(true)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full shadow-sm text-xs font-bold border transition-all duration-150 ${theme === "dark" ? "bg-[#1e1e22] text-white border-white/10 hover:bg-[#2a2a30]" : "bg-white text-gray-800 border-gray-200 hover:bg-gray-50"}`}
          >
            <History size={14} />
            {isEn ? "History" : "Historial"}
          </button>

          {/* Status Tag */}
          <div
            className={`flex items-center gap-1 text-xs font-[700] tracking-[0.05em] px-2 py-1 rounded-full border whitespace-nowrap transition-all duration-150 shadow-sm
              ${auth.status === "connected" ? "text-[var(--success)] bg-[var(--badge-m-bg)] border-[var(--border)]"
              : auth.status === "connecting" ? "text-[var(--warning)] bg-[var(--warning)]/10 border-[var(--warning)]/25"
              : auth.status === "error" ? "text-[var(--danger)] bg-[var(--danger)]/10 border-[var(--danger)]/25"
              : "text-[var(--text-dim)] bg-[var(--pill)] border-[var(--border)]"}`}
          >
            <div className={`w-[5px] h-[5px] rounded-full flex-shrink-0 
              ${auth.status === "connected" ? "bg-[var(--success)] animate-pulse shadow-[0_0_4px_var(--success)]"
              : auth.status === "connecting" ? "bg-[var(--warning)]"
              : auth.status === "error" ? "bg-[var(--danger)]"
              : "bg-[var(--text-dim)]"}`} 
            />
            <span>{auth.statusText}</span>
          </div>
        </div>

        <TranslationHistory
          translatorServiceUrl={auth.settings.translatorServiceUrl}
          recording={auth.recording}
          onOpenSavedSummary={modals.openSavedSummary}
          isOpen={modals.historyOpen}
          onClose={() => modals.setHistoryOpen(false)}
        />

        <div className="flex-1 flex flex-col min-w-0 bg-[var(--bg2)] relative order-1 lg:order-2 h-full">
          {auth.appMode === "traductor" && (
            <SubtitleView
              meetingTranscript={auth.meetingTranscript}
              subtitleOrig={auth.subtitleOrig}
              subtitleTran={auth.subtitleTran}
              srcLang={auth.srcLang}
              tgtLang={auth.tgtLang}
              recording={auth.recording}
              isConnecting={auth.status === "connecting"}
              onToggleRecording={handleToggleRecording}
            />
          )}

          {auth.appMode === "minuta" && (
            <MinutaView
              meetingTranscript={auth.meetingTranscript}
              subtitleOrig={auth.subtitleOrig}
              subtitleTran={auth.subtitleTran}
              tgtLang={auth.tgtLang}
              recording={auth.recording}
              isConnecting={auth.status === "connecting"}
              onOpenSummary={() => modals.setSummaryOpen(true)}
              onToggleRecording={handleToggleRecording}
            />
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="h-[36px] px-5 border-t border-[var(--b1)] bg-[var(--bg3)] flex items-center justify-between text-xs text-[var(--text-dim)] font-medium z-10 flex-shrink-0 transition-colors duration-300">
        <p>Kira v1.1 — Powered by HOLOCRUXE</p>
        <p className="font-mono text-xs">Deepgram Nova-3 · Google Translate · PiP</p>
      </footer>

      {/* Modals */}
      <ModalsContainer
        modals={modals}
        auth={auth}
        onStopConfirm={() => {
          auth.toggleRecording();
          modals.setStopConfirmOpen(false);
          if (auth.meetingTranscript.length > 0) {
            modals.setSummaryOpen(true);
          } else {
            auth.clearCurrentSession();
          }
        }}
        onLogoutConfirm={() => {
          if (auth.recording) {
            auth.toggleRecording();
          }
          login.handleLogout();
        }}
      />
    </div>
  );
}

// ── Root export: wraps everything with UiLangProvider ────────────────────────
export function App() {
  return (
    <UiLangProvider>
      <AppInner />
    </UiLangProvider>
  );
}

export default App;
